import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { ServiceIdsForMember, DefaultCareKind, AdditionalCareKind } from '@/types/seekerCC';
import { CLIENT_FEATURE_FLAGS, SEEKER_CHILD_CARE_ROUTES } from '@/constants';
import {
  useAppDispatch,
  useFlowState,
  useSeekerCCState,
  useSeekerState,
} from '@/components/AppState';
import useFlowNavigation from '@/components/features/flowNavigation/useFlowNavigation';
import { AnalyticsHelper } from '@/utilities/analyticsHelper';
import { logChildCareEvent } from '@/utilities/childCareAnalyticsHelper';
import { useFeatureFlags } from '@/components/FeatureFlagsContext';
import { useLoadInstantBookEligibleLocation } from '@/components/hooks/useLoadIBLocationEligible';
import { isFromVhpWithZip } from '@/components/hooks/useVhpMfeSharedData';
import { YesOrNoAnswer } from '@/__generated__/globalTypes';
import useEnterKey from '@/components/hooks/useEnterKey';
import { useChildcareDaycareBackup } from '@/components/hooks/useChildcareDaycareBackup';
import CareDatePage, { dayCareOptions, options } from '../../care-date/CareDate';

// @TODO extract and reuse for other components
const getOptionLabel = (optionsArray: any[], targetName: string) => {
  const seekOption = optionsArray.find((option: any) => option.value === targetName);
  return seekOption ? seekOption.label : targetName;
};

function CareDate() {
  const dispatch = useAppDispatch();
  const { careDate, enrollmentSource, czenServiceIdForMember, careKind } = useSeekerCCState();
  const { comeFrom } = useFlowState();
  const { goNext } = useFlowNavigation();
  const featureFlags = useFeatureFlags();
  const { isUserAssignedToSkipSteps, queryParamMetadata, dataTransferMetadata } = useSeekerState();
  const { isChildcareBackupCare } = useChildcareDaycareBackup();
  const router = useRouter();
  const isDayCare = czenServiceIdForMember === ServiceIdsForMember.dayCare;
  const noRushFlag = featureFlags?.flags[CLIENT_FEATURE_FLAGS.DAYCARE_JUST_BROWSING_VS_NO_RUSH];
  const dateOptions = isDayCare && noRushFlag?.value === 2 ? dayCareOptions : options;
  const disableNext = !careDate;
  useLoadInstantBookEligibleLocation();

  useEffect(() => {
    if (isDayCare) {
      AnalyticsHelper.logTestExposure(
        CLIENT_FEATURE_FLAGS.DAYCARE_JUST_BROWSING_VS_NO_RUSH,
        noRushFlag
      );
    }
  }, []);

  const handleNext = () => {
    const userEligibleForSkipStep =
      isUserAssignedToSkipSteps === YesOrNoAnswer.YES && queryParamMetadata?.careKindSupplied;

    if (isChildcareBackupCare && careKind === AdditionalCareKind.CHILDCARE_DAYCARE_BACKUP) {
      dispatch({ type: 'setCareKind', careKind: DefaultCareKind.NANNIES_RECURRING_BABYSITTERS });
    }

    const skipCareKind =
      userEligibleForSkipStep &&
      (careKind === DefaultCareKind.NANNIES_RECURRING_BABYSITTERS ||
        careKind === DefaultCareKind.ONE_TIME_BABYSITTERS);

    const skipNextStep =
      (isFromVhpWithZip(comeFrom, dataTransferMetadata) &&
        careKind !== DefaultCareKind.DAY_CARE_CENTERS) ||
      skipCareKind ||
      router.pathname === SEEKER_CHILD_CARE_ROUTES.INDEX;
    logChildCareEvent('Member Enrolled', 'Care Dates', enrollmentSource, {
      intent: getOptionLabel(options, careDate),
    });
    goNext(skipNextStep);
  };

  useEnterKey(true, () => handleNext(), true);

  return (
    <CareDatePage
      appAction="setCareDate"
      dateOptions={dateOptions}
      handleNext={handleNext}
      heading="When do you need care?"
      disabledNextButton={disableNext}
      selectedCareDate={careDate}
      selectorName="childCareDate"
    />
  );
}

export default CareDate;
